
import { Icon, Link, mergeStyleSets } from '@fluentui/react';
import { ExclamationTriangleIcon } from '@fluentui/react-icons-northstar';
import { Alert, Button, FilesUploadIcon, Flex, Grid, gridHorizontalBehavior, Image, Input, Layout, List, ListItem, Loader, Skeleton, Text, ThemePrepared } from '@fluentui/react-northstar';
import { themeNames } from '@fluentui/react-teams';
import * as microsoftTeams from "@microsoft/teams-js";
import React, { createRef, useCallback, useEffect, useRef, useState } from 'react';
import { buildStyles, CircularProgressbar } from 'react-circular-progressbar';
import "react-circular-progressbar/dist/styles.css";
import Dropzone, { DropzoneRef, FileRejection } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import EmptySVG from '../../../../../svg/empty.svg';
import { checkInTeams } from '../../../../App';
import { appState, IRequestPageConfig, useSetState } from '../../../../AppState';
import { getUser } from '../../../../AuthConfig';
import { AsyncQueue } from '../../../common/AsyncQueue';
import { platformService } from '../../../services/platform.service';
import { attachmentLocation } from '../../../utils/attachmentLocation';
import { uploadService } from '../../../services/upload.service';
import { EmptyData } from '../../EmptyData';
import { slateToMd } from '../../Wysiwyg/slate-editor-helpers';
import SlateEditor from '../../Wysiwyg/SlateEditor';
import { FileProps } from '../EditTicketAttachments';
import { getPhotoIconElement, iconElementFor, findIcon, resolveDocType, PHOTO_ICON_NAME } from './fileIcons';
import { forgetThumbnail, resolveThumbnail } from './thumbnailResolver';

const uploadFailedMessage = "Upload failed.";

interface UploadFromCustomFormProps {
  templateId?: number,
  templateData?: string;
  fromTemplate?: boolean;
  attachToCustomFormFromWeb?: boolean;
  isApplyTemplate?: boolean;
}

interface TicketPageProps {
  globalTheme: ThemePrepared<any>,
  ticketId: number,
  filesToUpload: File[];
  onInteraction?: (interaction: any) => void;
  teamsConfig: IRequestPageConfig;
  isEndUserPage: boolean;
  onClose?: (files?: FileProps[], optionalMessage?: string, isPrivate?: boolean) => void;
  uploadFromCustomFormProps?: UploadFromCustomFormProps
}

export const NewAttachmentFlowContent = (props: TicketPageProps) => {
  const state = appState();
  const setState = useSetState();
  const { t } = useTranslation();
  const { ticketId, globalTheme } = props;
  const { templateId, templateData, fromTemplate, attachToCustomFormFromWeb, isApplyTemplate } = props?.uploadFromCustomFormProps ?? {};

  const api = new platformService();
  const uploadHelper = new uploadService();

  const [files, setFiles] = useState<FileProps[]>([]);
  const droppedRef = useRef<FileProps[]>();
  droppedRef.current = files;
  const [uploadRenamedFiles, setUploadRenamedFiles] = useState<any[]>([]);
  const [triageChannel, setTriageChannel] = useState("");
  const [optionalMessage, setOptionalMessage] = useState([{
    children: [{ text: '' }],
  }
  ]);
  const [sendToChat, setSendToChat] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isAttaching, setIsAttaching] = useState(false);
  const [uploadDone, setUploadDone] = useState(false);
  const [assignees, setAssignees] = useState([]);
  const [droppedIndex, setDroppedIndex] = useState<number>();
  const droppedIndexRef = useRef<number>();
  droppedIndexRef.current = droppedIndex;  // This is wild that it's needed, but it is.

  let isInTeams = checkInTeams();

  const classNames = mergeStyleSets({
    fileLink: {
      fontWeight: 600,
      color: 'var(--mgt-theme-brand-foreground1)',
      ':hover, :active, :focus': {
        color: 'var(--mgt-theme-brand-foreground1) !important'
      }
    },
    alertLink: {
      color: 'inherit',
      fontWeight: 600,
      ':hover, :active, :focus': {
        color: 'inherit'
      }
    },
    fileIcon: {
      color: 'var(--mgt-theme-default-foreground2)',
      marginRight: ".5rem"
    },
    disabledContainer: {
      pointerEvents: 'none',
      opacity: 0.4
    }
  });

  const setFilesToUpload = (items: File[], files: FileProps[]) => {
    const renamedFiles = renameDuplicateFile(items, files);
    setUploadRenamedFiles(f => renamedFiles.concat(f));

    setFiles((currentItems: FileProps[]) => {
      let newItems: FileProps[] = renamedFiles.map(item =>
        Object.assign(item, {
          FileName: item.name,
          FileExtension: (item.ext.length > 0) ? item.ext : getExtension(item.name),
          FileSize: item?.file?.size ?? 0,
          isUploading: false
        } as FileProps));

      let allFiles = newItems.concat(currentItems);
      allFiles = allFiles.map(file => {
        if (!file.iconElement)
          file.iconElement = getFileIcon(file.FileExtension, file, globalTheme)
        return file;
      });
      return allFiles;
    });
  }

  useEffect(() => {
    (async () => {
      const currentUser = getUser();
      let result = props.teamsConfig?.TenantId ? props.teamsConfig : (await api.getTeamsBotConfiguration())?.data ?? {};
      setTriageChannel(result?.TriageChannel || "");

      if (props.filesToUpload?.length > 0 && files?.length == 0) {
        setFilesToUpload(props.filesToUpload, state.uploadingState.uploadedFiles);
      }

      if (state?.usersInRole?.length > 0)
        setAssignees(state.usersInRole.filter(a => a.UserName !== currentUser && a.Id !== -1));
      else if (!props.isEndUserPage) {
        try {
          const users = await api.getUsersInRole();
          setAssignees(JSON.parse(users.data.value).filter(a => a.UserName !== currentUser && a.Id !== -1));
        } catch {
          // Ignore this call if it fails, mentions just won't work.
        }
      }
      if (ticketId > 0) {
        try {
          let response = await api.getTicketFiles(ticketId);
          let idx = 0;

          for (const file of response) {
            if (file.FileName.indexOf('image-') > -1) {
              const indexNumber = parseInt(file.FileName.substring(file.FileName.indexOf('image-') + 'image-'.length, file.FileName.indexOf('image-') + 'image-'.length + 3));
              if (indexNumber > idx)
                idx = indexNumber;
            }
          }
          let newIndex = idx++;
          setDroppedIndex(newIndex);
        } catch {
          // Ignore this call if it fails, mentions just won't work.
        }
      }
      setIsLoading(false)
    })()
  }, []);

  const dropzoneRef = createRef<DropzoneRef>();
  const onDropAccepted = (items: File[]) => {
    setFilesToUpload(items, state.uploadingState.uploadedFiles.concat(files));
  }

  const pastedImageFile = (file: File) => {
    const result = new File([file], `image-${droppedIndexRef.current.toString().padStart(3, "0")}.png`, { type: file.type });
    setDroppedIndex((prevIndex) => prevIndex + 1);
    setFilesToUpload([result], state.uploadingState.uploadedFiles.concat(files));
  }

  const getExtension = (filename: string) => {
    return filename.substring(filename.lastIndexOf("."));
  };

  const updateFile = (index: number, progress: number) => {
    files[index].isInProgress = (progress < 100);
    files[index].progressPercent = progress;
    files[index].isUploading = (progress < 100);

    setFiles((items: FileProps[]) => {
      return items.map((item, idx) => {
        if (idx === index) {
          item.isInProgress = (progress < 100);
          item.progressPercent = progress;
          item.isUploading = (progress < 100);
        }
        return item;
      });
    });
    if (!isInTeams) {
      setState(s => ({
        ...s,
        uploadingState: {
          ...s.uploadingState,
          uploadedFiles: s.uploadingState.uploadedFiles.map(x => {
            if (x.FileName == files[index].FileName) {
              x.isInProgress = (progress < 100);
              x.progressPercent = progress;
              x.isUploading = (progress < 100);
            }
            return x;
          })
        }
      }));
    }
  }

  const onAttachTicket = (isPrivate?: boolean) => {
    setIsAttaching(true);

    setFiles((currentItems: FileProps[]) => {
      let newItems: FileProps[] = currentItems.map(item => Object.assign(item, { ModifiedDate: (new Date()).toString(), isUploading: true } as FileProps));
      if (!isInTeams) {
        setState((s) => ({
          ...s,
          uploadingState: {
            ...s.uploadingState,
            uploadedFiles: newItems.concat(s.uploadingState.uploadedFiles)
          }
        }))
      }
      return newItems;
    });

    if (attachToCustomFormFromWeb) {
      props.onClose(files, slateToMd(optionalMessage), isPrivate);
    } else {
      (async () => {
        let uploadedFiles: FileProps[];
        const filesToAttach = await startFileUploadSession(api, uploadHelper, uploadRenamedFiles, ticketId, fromTemplate, templateData, updateFile);

        if (!fromTemplate) {
          uploadedFiles = await api.attachTicketFiles(ticketId, filesToAttach, slateToMd(optionalMessage), isPrivate, props.isEndUserPage, sendToChat);
        }
        else {
          uploadedFiles = await api.attachBotCustomFormFiles(ticketId, templateId, filesToAttach, slateToMd(optionalMessage), isPrivate, templateData);
        }

        let errorOcurred = false;
        setFiles((currentItems) => {
          return currentItems.map(item => {
            if (item.isUploading) {
              const uploads = uploadedFiles.filter(file => file.FileName == item.FileName);
              if (uploads.length > 0) {
                item = uploads[0];
                item.isUploading = false;
              } else {
                item.isUploadFailed = true;
                item.isUploading = false;
                item.uploadFailedMessage = `${uploadFailedMessage}`;
                item.iconElement = getFileIcon('failed', item, globalTheme);
                errorOcurred = true;
              }
            }
            return item;
          });
        });

        if (!isInTeams) {
          if (!errorOcurred)
            toast.success(t('ticket-details.edit-ticket-view.ticket-updated-successfully', { ticketId: ticketId }));
          else
            toast.error(`Ticket #${ticketId}: ${uploadFailedMessage}`);

          setState((s) => ({
            ...s,
            uploadingState: {
              ...s.uploadingState, uploadedFiles: s.uploadingState.uploadedFiles.map(u => {
                const f = uploadedFiles.filter(x => x.FileName == u.FileName);
                return (f.length > 0) ? f[0] : u;
              })
            }
          }))

          if (props.onInteraction) props.onInteraction({ event: 'onAttachmentsUpdate' });
        }
        setUploadDone(true);
      })()
    }
  }

  const onDropRejected = (fileRejections: FileRejection[]) => {
    setFiles((currentItems: FileProps[]) => {
      let newItems: FileProps[] = fileRejections.map(rejected => {
        let item = rejected.file;
        return Object.assign(item, {
          FileName: item.name,
          isUploadFailed: true,
          uploadFailedMessage: `${uploadFailedMessage} ${rejected.errors.map(x => x.message).join(" ")}`,
          iconElement: getFileIcon('failed', item, globalTheme)
        } as FileProps)
      });
      return newItems.concat(currentItems);
    });
  }

  const renderSkeleton = (num: number) => Array.from(Array(num)).map((r, index) => (
    <ListItem {...{
      index: index,
      media: <Skeleton animation="wave"><Skeleton.Shape width="32px" height="32px"></Skeleton.Shape></Skeleton>,
      content: <Skeleton animation="wave"><Skeleton.Line></Skeleton.Line></Skeleton>
    }} />
  ));

  const renderFiles = files.map((file, index) => (
    <ListItem {...{
      index: index,
      media: ((file.isUploading) ?
        (file.isInProgress) ?
          <div style={{ width: '1.5rem', height: '1.5rem' }}><CircularProgressbar value={file.progressPercent} strokeWidth={10} styles={buildStyles({ pathColor: globalTheme.siteVariables.colorScheme.brand.foreground, trailColor: "rgba(0,0,0,0)" })} /></div> :
          <Loader size='smallest' inline /> :
        (file.iconElement) ? file.iconElement : getFileIcon((file.isUploadFailed) ? 'failed' : file.FileExtension, file, globalTheme)
      ),
      header: <Text content={getTruncatedFilename(file.FileName, 50)} />,
      headerMedia: (''),
      endMedia: <Grid accessibility={gridHorizontalBehavior} columns={1}>

        <Button
          disabled={isAttaching}
          icon={<Icon iconName="Delete" className={'text-sm'} />}
          iconOnly
          text
          title={t('ticket-details.edit-ticket-form.attachments.button.delete')}
          style={{
            color: !isAttaching ? globalTheme.siteVariables.colorScheme.brand.foreground : globalTheme.siteVariables.colorScheme.default.foregroundDisabled
          }}
          onClick={() => {
            setFiles((currentItems: FileProps[]) => {
              return currentItems.filter(item => item.FileName != file.FileName);
            });
            setUploadRenamedFiles((currentItems: FileProps[]) => {
              return currentItems.filter(item => item.FileName != file.FileName);
            });
          }}
        />
      </Grid>,
      content: (file.FileSize > 0) ? bytesToSize(file.FileSize) : "",
      style: {
        borderBottomWidth: 'thin',
        height: '4rem',
        display: file.isHidden ? 'none' : 'flex',
        cursor: 'auto'
      },
      hidden: file.isHidden
    }} />)
  );

  const onChangeEditorComponentCallback = useCallback((data: any) => { setOptionalMessage(data) }, [setOptionalMessage]);
  const closeTaskModlue = () => {
    if (isInTeams)
      microsoftTeams.dialog.url.submit();
    else if (props.onClose)
      props.onClose();
  };

  microsoftTeams.app.getContext().then((context: microsoftTeams.app.Context) => {
    setSendToChat(context.chat?.id?.length > 0)
  });

  let uploadHint = t('ticket-details.edit-ticket-form.attachments.hint.messagepart');

  if (attachToCustomFormFromWeb) {
    if (ticketId == 0)
      uploadHint = `${uploadHint} ${props.isEndUserPage ? t('ticket-details.edit-ticket-form.attachments.hint.submitrequest') : t('ticket-details.edit-ticket-form.attachments.hint.createticket')}.`;
    else
      uploadHint = `${uploadHint} ${isApplyTemplate ? t('ticket-details.edit-ticket-form.attachments.hint.applytemplate') : t('ticket-details.edit-ticket-form.attachments.hint.selectupdate')}.`;
  } else
    uploadHint = `${uploadHint} ${t('ticket-details.edit-ticket-form.attachments.hint.submitform')}.`;

  return (<>
    {(isLoading) && (
      <>
        <Skeleton animation="wave">
          <Flex space="between" style={{ margin: "1.25rem 0", alignItems: "center" }}>
            <Flex.Item className="extended-toolbar__far-side">
              <Flex vAlign="center">
                <Skeleton.Button />
              </Flex>
            </Flex.Item>
            <Flex.Item>
              <Skeleton.Input />
            </Flex.Item>
          </Flex>
        </Skeleton>
        <List>
          {renderSkeleton(10)}
        </List>
      </>
    )}
    {(!isLoading) && (
      <>
        {(triageChannel.length < 1) && (
          <Flex.Item>
            <Alert danger icon={<ExclamationTriangleIcon />} content={<>
              <Text content="Upload disabled. Please make sure you've added Tikit to a Team and set the Group (Channel) correctly. " />
              <Link target="_blank" href="https://get.cireson.com/how-do-i-add-tikit-to-a-team/" className={classNames.alertLink}>Learn more.</Link></>}
            />
          </Flex.Item>
        )}
        <Flex.Item grow>
          <Dropzone ref={dropzoneRef} noClick noKeyboard onDropAccepted={onDropAccepted} onDropRejected={onDropRejected} disabled={triageChannel.length < 1 || isAttaching}>
            {({ getRootProps, getInputProps, isDragActive }) => {
              return (
                (<div >
                  <div {...getRootProps({ className: 'dropzone overflow-auto' })} >
                    <input {...getInputProps()} />
                    {isDragActive && (
                      <Flex className={`h-screen w-full`} style={{
                        height: "100%",
                        left: 0,
                        top: 0,
                        backgroundColor: globalTheme.siteVariables.colorScheme.default.background,
                        opacity: "0.5",
                        zIndex: 2,
                        cursor: "pointer",
                        position: "absolute",
                        justifyContent: "center"
                      }}>
                        {(files.length > 0) && (
                          <Text content="Drop files here..." size="largest" weight="bold" style={{ alignSelf: "center" }} />
                        )}
                      </Flex>
                    )}
                    {(files.length > 0) && (
                      <>
                        <Flex.Item>
                          <Flex className='mt-2' style={{ background: globalTheme.siteVariables.colorScheme.brand.background1, color: globalTheme.siteVariables.colorScheme.default.foreground, padding: '4px 8px 4px 8px', justifyContent: 'space-between' }}>
                            {!isAttaching && (<Text content={t('ticket-details.edit-ticket-form.attachments.empty.sub-header-text')} weight="semibold" />)}
                            {isAttaching && (<Text content={`${!uploadDone ? `${(t('ticket-details.edit-ticket-form.attachments.upload-in-progress')).replace("...", '')}` : `${t('ticket-details.edit-ticket-form.attachments.button.upload')} ${t('common.done')}`}${(isInTeams && !uploadDone ? `, ${t('common.please-wait')}.` : `, ${t('requests.dialog.close-window').toLowerCase()}`)}`} weight="semibold" />)}
                          </Flex>
                        </Flex.Item>
                        <Flex.Item>
                          <Flex space="between" style={{ margin: "1.25rem 0", alignItems: "center" }}>
                            <Flex.Item className="extended-toolbar__far-side">
                              <Text content={`${t('ticket-details.edit-ticket-form.attachments.files-to-upload')} (${files.length})`} size="large" weight="semibold" />
                            </Flex.Item>
                            <Flex.Item >
                              <Flex vAlign="center">
                                <Button text icon={<FilesUploadIcon />} content={t('ticket-details.edit-ticket-form.attachments.button.upload')} title={t('ticket-details.edit-ticket-form.attachments.button.upload')} onClick={() => {
                                  if (dropzoneRef.current)
                                    dropzoneRef.current.open();
                                }}
                                  style={{
                                    color: !isAttaching ? globalTheme.siteVariables.colorScheme.brand.foreground : globalTheme.siteVariables.colorScheme.default.foregroundDisabled
                                  }}
                                  disabled={triageChannel.length < 1 || isAttaching}
                                />
                              </Flex>
                            </Flex.Item>
                          </Flex>
                        </Flex.Item>
                      </>
                    )}
                    {(files.length > 0 && !isLoading) && (
                      <>
                        <div
                          className='overflow-auto py-2'
                          style={{ maxHeight: '49vh' }}
                        >
                          <List truncateHeader variables={{ selectableFocusHoverBackgroundColor: globalTheme.siteVariables.colorScheme.default.background }}>
                            {renderFiles}
                          </List>
                        </div>
                        <Flex column className='mt-4'>
                          <Input.Label style={{ color: !isAttaching ? globalTheme.siteVariables.colorScheme.default.foreground : globalTheme.siteVariables.colorScheme.default.foregroundDisabled }}>{t('ticket-details.edit-ticket-form.attachments.optional-message')}</Input.Label>
                          <div className={`border ${(isAttaching || files.length == 0) ? classNames.disabledContainer : ""}`}>
                            <SlateEditor
                              readOnly={isAttaching || files.length == 0}
                              onChange={onChangeEditorComponentCallback}
                              slateValue={optionalMessage}
                              usersInMention={assignees}
                              onImagePaste={pastedImageFile}
                              isFocus={true}
                              noImage={true}
                              minHeight='5rem'
                            />
                          </div>
                          <div className='mt-3 flex flex-col items-center gap-2 md:flex-row md:justify-end'>
                            <Button className='w-full md:w-auto' content={t('common.buttons.close')} onClick={closeTaskModlue} disabled={isAttaching && !uploadDone && isInTeams}
                              style={isAttaching && !uploadDone && isInTeams ? { color: globalTheme.siteVariables.colorScheme.default.foregroundDisabled, background: globalTheme.siteVariables.theme === themeNames.Dark && isInTeams ? globalTheme.siteVariables.colorScheme.default.background : '' } : {}} />
                            {!props.isEndUserPage && (
                              <Button className='w-full md:w-auto' disabled={isAttaching || files.length == 0} content={t('ticket-details.edit-ticket-form.attachments.add-internal-attachment')} tinted onClick={() => onAttachTicket(true)}
                                style={isAttaching || files.length == 0 ? { color: globalTheme.siteVariables.colorScheme.default.foregroundDisabled, background: globalTheme.siteVariables.theme === themeNames.Dark && isInTeams ? globalTheme.siteVariables.colorScheme.default.background : '' } : {}}
                              />
                            )}
                            <Button className='w-full md:w-auto' disabled={isAttaching || files.length == 0} content={!fromTemplate || isInTeams ? t('ticket-details.edit-ticket-form.attachments.attach-to-ticket') : t('ticket-details.edit-ticket-form.custom-form-answer.attachto-customform')} primary onClick={() => onAttachTicket(false)}
                              style={isAttaching || files.length == 0 ? { color: globalTheme.siteVariables.colorScheme.default.foregroundDisabled, background: globalTheme.siteVariables.theme === themeNames.Dark && isInTeams ? globalTheme.siteVariables.colorScheme.default.background : '' } : {}} />
                          </div>
                        </Flex>
                        {(fromTemplate && !isInTeams) && (
                          <Flex hAlign='end' className='mt-1' gap="gap.small">
                            <Text content={uploadHint} style={{ color: !isAttaching ? globalTheme.siteVariables.colorScheme.default.foreground : globalTheme.siteVariables.colorScheme.default.foregroundDisabled }} />
                          </Flex>
                        )}
                      </>
                    )}
                    {(files.length == 0) && (
                      <div className={`py-10`} style={{ height: '72vh' }} >
                        <EmptyData
                          headerText={t('ticket-details.edit-ticket-form.attachments.empty.header-text')}
                          subheaderText={t('ticket-details.edit-ticket-form.attachments.empty.sub-header-text')}
                          SVGIcon={<EmptySVG height={120} width={120} />}
                          Extras={<Button className='mt-2'
                            content={t('ticket-details.edit-ticket-form.attachments.button.upload')}
                            primary
                            onClick={() => {
                              if (dropzoneRef.current)
                                dropzoneRef.current.open();
                            }}
                          />}
                        />
                      </div>
                    )}
                  </div>
                </div>)
              );
            }}
          </Dropzone>
        </Flex.Item>
      </>
    )}
  </>);

}

export const startFileUploadSession = async (api: platformService, uploadHelper: uploadService, uploadRenamedFiles: any[], ticketId: number, uploadAsTemporary: boolean, templateData: string, updateFile?: any) => {

  const jobs = [];
  const queue = AsyncQueue(2);
  uploadRenamedFiles.forEach((file, idx) => jobs.push(queue.add(async () => {
    const upload = await api.getUploadUrl(ticketId, file.FileName, file.FileSize, uploadAsTemporary, templateData);
    const url = upload.data;

    if (updateFile) updateFile(idx, 0);

    const response = await uploadHelper.uploadFile(url.uploadUrl, file, (u, t) => { if (updateFile) updateFile(idx, (u * 1.0 / t) * 100) });
    response.data.groupId = url.groupId;
    response.data.driveId = url.driveId;
    if (updateFile) updateFile(idx, 100);
    return response;
  })));

  const results = await Promise.allSettled(jobs);

  const filesToAttach = results
    .map(file => (file.status == "fulfilled" && file.value.data.id) ?
      { FileName: file.value.data.name, FileSize: file.value.data.size, DriveItemId: file.value.data.id, ...attachmentLocation(file.value.data), DownladUrl: file.value.data.webUrl } :
      null)
    .filter(x => x !== null);

  return filesToAttach;
}

// Thumbnails are SharePoint links minted when the file was uploaded, and they expire, so a
// preview can fail long after the attachment itself is still fine. Pointing an <img> straight
// at one paints the browser's broken glyph before we can react, so the link is checked
// off-screen first and only a working one is ever rendered. The slot shows a skeleton while
// that happens, and resolves once - it never shows one answer and then a different one.
const AttachmentThumbnail = ({ link, attachmentId, theme }: { link: string, attachmentId?: number, theme: any }) => {
  // undefined while resolving, a url once one is known to load, null when there is none.
  const [resolved, setResolved] = useState<string | null | undefined>(undefined);
  const retried = useRef(false);

  useEffect(() => {
    let cancelled = false;
    retried.current = false;

    // A file still being uploaded has a local object URL and no attachment id yet; there is
    // nothing to verify or refresh, so show it straight away.
    if (!attachmentId) {
      setResolved(link || null);
      return;
    }

    setResolved(undefined);
    resolveThumbnail({ Id: attachmentId, ThumbnailUrl: link })
      .then(url => { if (!cancelled) setResolved(url); })
      .catch(() => { if (!cancelled) setResolved(null); });

    return () => { cancelled = true; };
  }, [link, attachmentId]);

  // A remembered link is handed back without re-verifying, so it can be one that has expired
  // since. Forget it and resolve once more - that path verifies and refreshes - rather than
  // falling back to the icon and leaving the dead entry to fail again on every later visit.
  const onImageError = async () => {
    if (retried.current || !attachmentId) {
      setResolved(null);
      return;
    }
    retried.current = true;
    forgetThumbnail(attachmentId);
    setResolved(undefined);

    try {
      setResolved(await resolveThumbnail({ Id: attachmentId, ThumbnailUrl: link }));
    } catch {
      setResolved(null);
    }
  };

  if (resolved === undefined)
    return (
      <Skeleton animation="wave">
        <Skeleton.Shape width="20px" height="20px" />
      </Skeleton>
    );

  if (!resolved)
    return getPhotoIconElement(theme);

  return (
    <Layout
      styles={{
        maxWidth: '20px',
      }}
      renderMainArea={() => (
        <Image
          fluid
          src={resolved}
          onError={onImageError}
        />
      )}
    />
  );
}

export const getImageElement = (attachment: FileProps, theme?: any) => {
  let link = "";
  try {
    // DriveItemId is what says a file has been uploaded. A group id no longer does: a channel-scoped
    // attachment has none, and reading its absence as "still local" throws on createObjectURL(undefined).
    link = (!attachment.DriveItemId) ? URL.createObjectURL(attachment["file"]) : (attachment.ThumbnailUrl ?? "");
  } catch { }
  return <AttachmentThumbnail link={link} attachmentId={attachment.Id} theme={theme} />;
}

export const getFileIcon = (ext: string, file: any, theme: any) => {
  const icon = findIcon(resolveDocType(ext, file));
  if (icon.name === PHOTO_ICON_NAME)
    return getImageElement(file, theme);
  return iconElementFor(icon, theme);
}

const bytesToSize = (bytes: number) => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = 2;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

const renameDuplicateFile = (items: File[], files: FileProps[]) => {
  return items.map(file => {
    const filenames = files.map(item => {
      if (item.isUploadFailed) return -1;

      let myRegexp = /^(.*?)\((\d+)\)\.(.*)$/g;
      let match = myRegexp.exec(item.FileName);
      if (match != null) {
        const name = match[1];
        const num = match[2];
        const ext = match[3];
        if (file.name.replace(/\s/g, '') == `${name}.${ext}`.replace(/\s/g, ''))
          return (num) ? parseInt(num) : 0;
        else
          return -1;
      }

      if (file.name.replace(/\s/g, '') == item.FileName.replace(/\s/g, ''))
        return 0;
      else
        return -1;
    });
    let filename = file.name;
    let ext = "";
    let max = Math.max.apply(Math, filenames);
    if (max > -1) {
      const lastIndex = file.name.lastIndexOf(".");
      const ext = file.name.substring(lastIndex);
      filename = `${file.name.substring(0, lastIndex)} (${max + 1})${ext}`;
    }
    return {
      file: file,
      name: filename,
      ext: ext
    }
  });
}

export const getTruncatedFilename = (filename: string, length?: number) => {
  const len = length ?? 21;
  const split = filename.split('.');
  let name = split[0];
  const extension = split[1];

  if (name.length > len) {
    name = name.substring(0, len);
    filename = name + '....' + extension;
  }

  return filename;
} 